// ══════════════════════════════════════════════════════════════════════════════
// RÉSEAU PRO B2B - Feed exclusif pour les professionnels
// Posts, Sous-traitance, Partenariats, Projets, Opportunités
// ══════════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useOutletContext, Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Heart, MessageCircle, Share2, Bookmark, MoreHorizontal, Image,
  Send, Smile, ThumbsUp, Users, Star, MapPin, Calendar, TrendingUp,
  PlusCircle, Filter, RefreshCw, BadgeCheck, Building2, Wrench,
  Network, Handshake, Briefcase, Target, Zap, Euro, Clock,
  ChevronDown, X, FileText, Sparkles, ArrowLeft, ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { MatchingButton, MatchingOffersWidget } from '../../components/pro/MatchingComponents';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Types de posts Pro
const POST_TYPES = [
  { id: 'all', label: 'Tout', icon: Network },
  { id: 'publication', label: 'Publications', icon: FileText },
  { id: 'sous_traitance', label: 'Sous-traitance', icon: Wrench },
  { id: 'partenariat', label: 'Partenariats', icon: Handshake },
  { id: 'projet', label: 'Projets', icon: Building2 },
  { id: 'opportunite', label: 'Opportunités', icon: Target },
  { id: 'conseil', label: 'Conseils', icon: Zap },
];

// Badge type post
const TYPE_BADGES = {
  publication: { label: '📢 Publication', bg: 'bg-blue-100', text: 'text-blue-700' },
  sous_traitance: { label: '🔧 Sous-traitance', bg: 'bg-orange-100', text: 'text-orange-700' },
  partenariat: { label: '🤝 Partenariat', bg: 'bg-purple-100', text: 'text-purple-700' },
  projet: { label: '🏗️ Projet', bg: 'bg-green-100', text: 'text-green-700' },
  opportunite: { label: '🎯 Opportunité', bg: 'bg-amber-100', text: 'text-amber-700' },
  conseil: { label: '💡 Conseil', bg: 'bg-cyan-100', text: 'text-cyan-700' },
};

// Composant Post Pro
function ProPostCard({ post, onLike, onSave, onComment }) {
  const [showComments, setShowComments] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [isLiked, setIsLiked] = useState(post.is_liked);
  const [isSaved, setIsSaved] = useState(post.is_saved);
  const [likesCount, setLikesCount] = useState(post.likes);

  const handleLike = () => {
    setIsLiked(!isLiked);
    setLikesCount(prev => isLiked ? prev - 1 : prev + 1);
    onLike(post.id);
  };

  const handleSave = () => {
    setIsSaved(!isSaved);
    onSave(post.id);
  };

  const badge = TYPE_BADGES[post.post_type] || TYPE_BADGES.publication;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden"
    >
      {/* Header */}
      <div className="p-4 flex items-start gap-3">
        <div className="w-12 h-12 rounded-full bg-gradient-to-br from-[#1E3A5F] to-[#2d5a8a] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
          {post.author?.avatar || 'P'}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-semibold text-gray-900">{post.author?.name || 'Pro'}</span>
            {post.author?.verified && (
              <BadgeCheck size={16} className="text-[#1E3A5F]" />
            )}
          </div>
          <div className="flex items-center gap-2 text-sm text-gray-500">
            <span>{post.author?.metier || 'Artisan'}</span>
            <span>•</span>
            <div className="flex items-center gap-1">
              <Star size={12} className="text-amber-500 fill-amber-500" />
              <span>{post.author?.rating || '4.5'}</span>
            </div>
            <span>•</span>
            <span>{post.time}</span>
          </div>
        </div>
        <button className="p-2 hover:bg-gray-100 rounded-full transition-colors">
          <MoreHorizontal size={20} className="text-gray-400" />
        </button>
      </div>

      {/* Content */}
      <div className="px-4 pb-3">
        <p className="text-gray-800 whitespace-pre-line">{post.content}</p>
      </div>

      {/* Images */}
      {post.images && post.images.length > 0 && (
        <div className={`${post.images.length > 1 ? 'grid grid-cols-2 gap-1' : ''}`}>
          {post.images.map((img, index) => (
            <img
              key={index}
              src={img}
              alt=""
              className="w-full h-64 object-cover"
            />
          ))}
        </div>
      )}

      {/* Infos supplémentaires (sous-traitance, etc.) */}
      {(post.budget || post.location || post.deadline || post.skills_required?.length > 0) && (
        <div className="px-4 py-3 bg-gray-50 border-t border-gray-100">
          <div className="flex flex-wrap gap-3 text-sm">
            {post.budget && (
              <div className="flex items-center gap-1 text-green-600">
                <Euro size={14} />
                <span className="font-medium">{post.budget}</span>
              </div>
            )}
            {post.location && (
              <div className="flex items-center gap-1 text-gray-600">
                <MapPin size={14} />
                <span>{post.location}</span>
              </div>
            )}
            {post.deadline && (
              <div className="flex items-center gap-1 text-orange-600">
                <Clock size={14} />
                <span>{post.deadline}</span>
              </div>
            )}
          </div>
          {post.skills_required?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {post.skills_required.map((skill, i) => (
                <span key={i} className="px-2 py-0.5 bg-[#1E3A5F]/10 text-[#1E3A5F] text-xs rounded-full">
                  {skill}
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Type badge + stats */}
      <div className="px-4 py-2 flex items-center justify-between border-t border-gray-100">
        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${badge.bg} ${badge.text}`}>
          {badge.label}
        </span>
        <div className="flex items-center gap-4 text-sm text-gray-500">
          <div className="flex items-center gap-1">
            <ThumbsUp size={14} className="text-[#1E3A5F]" />
            <span>{likesCount}</span>
          </div>
          <span>{post.comments} commentaires</span>
        </div>
      </div>

      {/* Actions */}
      <div className="px-4 py-2 flex items-center justify-between border-t border-gray-100">
        <button
          onClick={handleLike}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            isLiked ? 'text-[#F97316] bg-orange-50' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Heart size={20} className={isLiked ? 'fill-current' : ''} />
          <span className="text-sm font-medium">J'aime</span>
        </button>
        <button
          onClick={() => setShowComments(!showComments)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors"
        >
          <MessageCircle size={20} />
          <span className="text-sm font-medium">Commenter</span>
        </button>
        <button className="flex items-center gap-2 px-4 py-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors">
          <Share2 size={20} />
          <span className="text-sm font-medium hidden sm:inline">Partager</span>
        </button>
        <button
          onClick={handleSave}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
            isSaved ? 'text-[#1E3A5F] bg-blue-50' : 'text-gray-600 hover:bg-gray-100'
          }`}
        >
          <Bookmark size={20} className={isSaved ? 'fill-current' : ''} />
        </button>
      </div>

      {/* Contact button for sous-traitance/partenariat */}
      {(post.post_type === 'sous_traitance' || post.post_type === 'partenariat' || post.post_type === 'opportunite') && (
        <div className="px-4 pb-4 flex gap-2">
          <Button className="flex-1 bg-[#F97316] hover:bg-[#ea6c0a] text-white">
            <Send size={16} className="mr-2" />
            Contacter
          </Button>
          <MatchingButton postId={post.id} postType={post.post_type} />
        </div>
      )}

      {/* Comments section */}
      <AnimatePresence>
        {showComments && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="border-t border-gray-100 overflow-hidden"
          >
            <div className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-full bg-[#1E3A5F] flex items-center justify-center text-white text-xs font-bold">
                  ME
                </div>
                <div className="flex-1 relative">
                  <input
                    type="text"
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Écrire un commentaire..."
                    className="w-full px-4 py-2 pr-20 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
                  />
                  <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1">
                    <button className="p-1.5 hover:bg-gray-200 rounded-full">
                      <Smile size={16} className="text-gray-400" />
                    </button>
                    <button 
                      onClick={() => {
                        if (newComment.trim()) {
                          onComment(post.id, newComment);
                          setNewComment('');
                        }
                      }}
                      className="p-1.5 bg-[#F97316] hover:bg-[#ea6c0a] rounded-full"
                    >
                      <Send size={14} className="text-white" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
}

// Modal pour créer un post Pro - Version mobile plein écran / Desktop centré
function CreateProPostModal({ isOpen, onClose, onSubmit }) {
  const [postType, setPostType] = useState('publication');
  const [content, setContent] = useState('');
  const [budget, setBudget] = useState('');
  const [location, setLocation] = useState('');
  const [deadline, setDeadline] = useState('');
  const [skills, setSkills] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMobile] = useState(window.innerWidth < 768);
  const [keyboardHeight, setKeyboardHeight] = useState(0);

  // Détection du clavier et masquage navbar
  useEffect(() => {
    if (!isOpen || !isMobile) {
      setKeyboardHeight(0);
      return;
    }
    
    document.body.classList.add('modal-fullscreen-open');
    
    const viewport = window.visualViewport;
    if (!viewport) return;
    
    const handleViewportResize = () => {
      const heightDiff = window.innerHeight - viewport.height;
      setKeyboardHeight(heightDiff > 50 ? heightDiff : 0);
    };
    
    viewport.addEventListener('resize', handleViewportResize);
    viewport.addEventListener('scroll', handleViewportResize);
    
    return () => {
      document.body.classList.remove('modal-fullscreen-open');
      viewport.removeEventListener('resize', handleViewportResize);
      viewport.removeEventListener('scroll', handleViewportResize);
    };
  }, [isOpen, isMobile]);

  const handleSubmit = async () => {
    if (!content.trim()) {
      toast.error('Écris quelque chose !');
      return;
    }

    setIsLoading(true);
    try {
      await onSubmit({
        content,
        post_type: postType,
        budget: budget || undefined,
        location: location || undefined,
        deadline: deadline || undefined,
        skills_required: skills ? skills.split(',').map(s => s.trim()) : [],
      });
      onClose();
      setContent('');
      setBudget('');
      setLocation('');
      setDeadline('');
      setSkills('');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  // Version mobile plein écran
  if (isMobile) {
    return createPortal(
      <motion.div
        initial={{ y: '100%' }}
        animate={{ y: 0 }}
        exit={{ y: '100%' }}
        transition={{ duration: 0.25, ease: 'easeOut' }}
        className="fixed inset-0 z-[10002] flex flex-col bg-white"
      >
        {/* Header */}
        <header className="flex items-center gap-2 px-3 py-3 border-b border-gray-100">
          <button onClick={onClose} className="p-1 text-gray-500">
            <ArrowLeft size={22} />
          </button>
          <h1 className="text-[15px] font-bold text-gray-900">Nouvelle publication Pro</h1>
        </header>

        {/* Contenu scrollable */}
        <div className="flex-1 overflow-y-auto p-5">
          {/* Type de post */}
          <div className="mb-4">
            <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Type de publication</label>
            <div className="grid grid-cols-3 gap-2">
              {POST_TYPES.filter(t => t.id !== 'all').map(type => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    onClick={() => setPostType(type.id)}
                    className={`p-2 rounded-lg border-2 text-center transition-all text-sm ${
                      postType === type.id
                        ? 'border-[#1E3A5F] bg-[#F0F4F8]'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Icon size={18} className="mx-auto mb-1" />
                    <span className="text-xs">{type.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Contenu */}
          <div className="mb-4">
            <label className="block text-[12px] font-semibold mb-2 uppercase tracking-wide text-gray-500">Contenu *</label>
            <textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder={
                postType === 'sous_traitance' ? 'Décris ta mission de sous-traitance...' :
                postType === 'partenariat' ? 'Décris le partenariat recherché...' :
                postType === 'projet' ? 'Présente ton projet...' :
                'Partage avec les pros...'
              }
              rows={5}
              className="w-full p-4 rounded-xl border text-[16px] bg-gray-50 border-gray-200 text-gray-900 placeholder-gray-400 resize-none"
            />
          </div>

          {/* Champs supplémentaires pour sous-traitance/partenariat */}
          {(postType === 'sous_traitance' || postType === 'partenariat' || postType === 'opportunite') && (
            <div className="space-y-3 mb-4 p-4 bg-gray-50 rounded-xl">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-[12px] font-semibold mb-1 uppercase tracking-wide text-gray-500">Budget</label>
                  <input
                    type="text"
                    value={budget}
                    onChange={e => setBudget(e.target.value)}
                    placeholder="Ex: 5 000 €"
                    className="w-full p-3 rounded-xl border text-[16px] bg-white border-gray-200 text-gray-900 placeholder-gray-400"
                  />
                </div>
                <div>
                  <label className="block text-[12px] font-semibold mb-1 uppercase tracking-wide text-gray-500">Localisation</label>
                  <input
                    type="text"
                    value={location}
                    onChange={e => setLocation(e.target.value)}
                    placeholder="Ex: Nice (06)"
                    className="w-full p-3 rounded-xl border text-[16px] bg-white border-gray-200 text-gray-900 placeholder-gray-400"
                  />
                </div>
              </div>
              <div>
                <label className="block text-[12px] font-semibold mb-1 uppercase tracking-wide text-gray-500">Délai</label>
                <input
                  type="text"
                  value={deadline}
                  onChange={e => setDeadline(e.target.value)}
                  placeholder="Ex: 3 semaines"
                  className="w-full p-3 rounded-xl border text-[16px] bg-white border-gray-200 text-gray-900 placeholder-gray-400"
                />
              </div>
              <div>
                <label className="block text-[12px] font-semibold mb-1 uppercase tracking-wide text-gray-500">Compétences recherchées (séparées par des virgules)</label>
                <input
                  type="text"
                  value={skills}
                  onChange={e => setSkills(e.target.value)}
                  placeholder="Ex: Carreleur, Grand format, Expérimenté"
                  className="w-full p-3 rounded-xl border text-[16px] bg-white border-gray-200 text-gray-900 placeholder-gray-400"
                />
              </div>
            </div>
          )}
        </div>

        {/* Footer fixe */}
        <div 
          className="p-4 border-t border-gray-100 bg-white transition-all duration-200"
          style={{ 
            paddingBottom: keyboardHeight > 0 ? '16px' : 'calc(16px + env(safe-area-inset-bottom, 0px))',
            marginBottom: keyboardHeight > 0 ? `${keyboardHeight}px` : '0px'
          }}
        >
          <button
            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 mb-3 text-sm font-medium rounded-xl transition-all text-orange-600 bg-orange-50 hover:bg-orange-100 border border-orange-200"
          >
            <Sparkles size={16} />
            Créer avec Abracada
          </button>
          
          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 h-12 rounded-xl font-semibold transition-all border border-gray-300 text-[#1e3a5f] hover:bg-gray-100"
            >
              Annuler
            </button>
            <button
              onClick={handleSubmit}
              disabled={!content.trim() || isLoading}
              className="flex-1 h-12 bg-orange-500 hover:bg-orange-600 text-white rounded-xl font-semibold shadow-lg shadow-orange-500/25 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <>
                  <Send size={18} />
                  Publier
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>,
      document.body
    );
  }

  // Version desktop
  return createPortal(
    <div className="fixed inset-0 bg-black/60 z-[10002] flex items-center justify-center p-4" onClick={onClose}>
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-2xl w-full max-w-lg shadow-2xl overflow-hidden"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-[#1E3A5F] to-[#2d5a8a]">
          <h2 className="font-semibold text-white">Nouvelle publication Pro</h2>
          <button onClick={onClose} className="p-2 hover:bg-white/20 rounded-lg text-white">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 max-h-[70vh] overflow-y-auto">
          {/* Type de post */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">Type de publication</label>
            <div className="grid grid-cols-3 gap-2">
              {POST_TYPES.filter(t => t.id !== 'all').map(type => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    onClick={() => setPostType(type.id)}
                    className={`p-2 rounded-lg border-2 text-center transition-all text-sm ${
                      postType === type.id
                        ? 'border-[#1E3A5F] bg-[#F0F4F8]'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <Icon size={18} className="mx-auto mb-1" />
                    <span className="text-xs">{type.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Contenu */}
          <div className="mb-4">
            <textarea
              value={content}
              onChange={e => setContent(e.target.value)}
              placeholder={
                postType === 'sous_traitance' ? 'Décris ta mission de sous-traitance...' :
                postType === 'partenariat' ? 'Décris le partenariat recherché...' :
                postType === 'projet' ? 'Présente ton projet...' :
                'Partage avec les pros...'
              }
              rows={4}
              className="w-full px-4 py-3 border border-gray-200 rounded-xl resize-none focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
            />
          </div>

          {/* Champs supplémentaires pour sous-traitance/partenariat */}
          {(postType === 'sous_traitance' || postType === 'partenariat' || postType === 'opportunite') && (
            <div className="space-y-3 mb-4 p-4 bg-gray-50 rounded-xl">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Budget</label>
                  <input
                    type="text"
                    value={budget}
                    onChange={e => setBudget(e.target.value)}
                    placeholder="Ex: 5 000 €"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">Localisation</label>
                  <input
                    type="text"
                    value={location}
                    onChange={e => setLocation(e.target.value)}
                    placeholder="Ex: Nice (06)"
                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
                  />
                </div>
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Délai</label>
                <input
                  type="text"
                  value={deadline}
                  onChange={e => setDeadline(e.target.value)}
                  placeholder="Ex: 3 semaines"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Compétences recherchées (séparées par des virgules)</label>
                <input
                  type="text"
                  value={skills}
                  onChange={e => setSkills(e.target.value)}
                  placeholder="Ex: Carreleur, Grand format, Expérimenté"
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#1E3A5F]/20"
                />
              </div>
            </div>
          )}

          {/* Bouton publier */}
          <Button
            onClick={handleSubmit}
            disabled={!content.trim() || isLoading}
            className="w-full bg-[#F97316] hover:bg-[#ea6c0a] text-white py-3"
          >
            {isLoading ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <>
                <Send size={18} className="mr-2" />
                Publier
              </>
            )}
          </Button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
}

// Page principale
export default function ReseauProPage() {
  const context = useOutletContext();
  const [posts, setPosts] = useState([]);
  const [activeFilter, setActiveFilter] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  
  // État pour le scroll des filtres mobile
  const [showScrollHint, setShowScrollHint] = useState(true);
  const filterScrollRef = useRef(null);
  
  const handleFilterScroll = () => {
    if (filterScrollRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = filterScrollRef.current;
      setShowScrollHint(scrollLeft < scrollWidth - clientWidth - 10);
    }
  };

  const token = context?.token || localStorage.getItem('client_token');

  // Charger les posts
  useEffect(() => {
    loadPosts();
  }, [activeFilter]);

  const loadPosts = async () => {
    setIsLoading(true);
    try {
      const params = new URLSearchParams();
      if (activeFilter !== 'all') params.append('post_type', activeFilter);
      
      const response = await fetch(`${API_URL}/api/reseau-pro/feed?${params}`, {
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      
      if (response.ok) {
        const data = await response.json();
        setPosts(data.posts || []);
      }
    } catch (error) {
      console.error('Error loading posts:', error);
    } finally {
      setIsLoading(false);
    }
  };

  // État vide production - pas de seed automatique de données démo

  // Like
  const handleLike = async (postId) => {
    try {
      await fetch(`${API_URL}/api/reseau-pro/feed/${postId}/like`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
    } catch (error) {
      console.error('Error liking post:', error);
    }
  };

  // Save
  const handleSave = (postId) => {
    toast.success('Post sauvegardé !');
  };

  // Comment
  const handleComment = async (postId, content) => {
    try {
      const response = await fetch(`${API_URL}/api/reseau-pro/feed/${postId}/comment?content=${encodeURIComponent(content)}`, {
        method: 'POST',
        headers: token ? { 'Authorization': `Bearer ${token}` } : {}
      });
      if (response.ok) {
        toast.success('Commentaire ajouté !');
        loadPosts();
      }
    } catch (error) {
      console.error('Error commenting:', error);
    }
  };

  // Create post
  const handleCreatePost = async (data) => {
    try {
      const response = await fetch(`${API_URL}/api/reseau-pro/feed`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { 'Authorization': `Bearer ${token}` } : {})
        },
        body: JSON.stringify(data)
      });
      
      if (response.ok) {
        toast.success('Publication créée !');
        loadPosts();
      } else {
        throw new Error('Erreur');
      }
    } catch (error) {
      toast.error('Erreur lors de la publication');
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Network size={24} className="text-[#1E3A5F]" />
            Réseau Pro
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Feed exclusif pour les professionnels du bâtiment
          </p>
        </div>
        <Button
          onClick={() => setShowCreateModal(true)}
          className="bg-[#F97316] hover:bg-[#ea6c0a] text-white"
        >
          <PlusCircle size={18} className="mr-2" />
          Publier
        </Button>
      </div>

      {/* Quick stats - visible seulement s'il y a des posts */}
      {posts.length > 0 && (
        <div className="grid grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-orange-100 flex items-center justify-center">
                <Wrench size={20} className="text-[#F97316]" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Sous-traitance</p>
                <p className="font-bold text-gray-900">{posts.filter(p => p.post_type === 'sous_traitance').length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                <Handshake size={20} className="text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Partenariats</p>
                <p className="font-bold text-gray-900">{posts.filter(p => p.post_type === 'partenariat').length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-gray-100">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Target size={20} className="text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Opportunités</p>
                <p className="font-bold text-gray-900">{posts.filter(p => p.post_type === 'opportunite').length}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filters - Style premium avec scroll horizontal */}
      <div className="mb-6">
        {/* Mobile */}
        <div className="sm:hidden">
          <div className="relative flex items-center">
            <div 
              ref={filterScrollRef}
              onScroll={handleFilterScroll}
              className="flex gap-1.5 overflow-x-auto scrollbar-hide flex-1 pr-6"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {POST_TYPES.map((type) => {
                const Icon = type.icon;
                return (
                  <button
                    key={type.id}
                    onClick={() => setActiveFilter(type.id)}
                    data-testid={`filter-${type.id}`}
                    className={`flex-shrink-0 px-3 py-2 rounded-xl text-xs font-semibold transition-all whitespace-nowrap flex items-center gap-1.5 ${
                      activeFilter === type.id
                        ? 'bg-orange-500 text-white shadow-sm'
                        : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200'
                    }`}
                  >
                    <Icon size={12} />
                    {type.label}
                  </button>
                );
              })}
            </div>
            
            {/* Flèche orange discrète */}
            {showScrollHint && (
              <div className="flex-shrink-0 flex items-center pointer-events-none">
                <ChevronRight size={18} className="text-orange-500" strokeWidth={2.5} />
              </div>
            )}
          </div>
        </div>
        
        {/* Desktop */}
        <div className="hidden sm:flex flex-wrap gap-2">
          {POST_TYPES.map((type) => {
            const Icon = type.icon;
            return (
              <button
                key={type.id}
                onClick={() => setActiveFilter(type.id)}
                className={`px-4 py-2 rounded-xl text-sm font-medium transition-colors flex items-center gap-2 ${
                  activeFilter === type.id
                    ? 'bg-orange-500 text-white'
                    : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
              >
                <Icon size={14} />
                {type.label}
              </button>
            );
          })}
        </div>
      </div>

      {/* Loading */}
      {isLoading && (
        <div className="flex justify-center py-12">
          <div className="w-8 h-8 border-4 border-[#1E3A5F] border-t-transparent rounded-full animate-spin" />
        </div>
      )}

      {/* Posts */}
      {!isLoading && (
        <div className="space-y-4">
          {/* Widget "Offres pour toi" - matching automatique */}
          <MatchingOffersWidget />
          
          {posts.length === 0 ? (
            <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
              <Network size={48} className="text-gray-300 mx-auto mb-4" />
              <h3 className="font-semibold text-gray-700 mb-2">Aucune publication</h3>
              <p className="text-gray-500 text-sm mb-4">Sois le premier à publier !</p>
              <Button onClick={() => setShowCreateModal(true)} className="bg-[#F97316]">
                <PlusCircle size={18} className="mr-2" />
                Créer une publication
              </Button>
            </div>
          ) : (
            posts.map((post) => (
              <ProPostCard
                key={post.id}
                post={post}
                onLike={handleLike}
                onSave={handleSave}
                onComment={handleComment}
              />
            ))
          )}
        </div>
      )}

      {/* Load more */}
      {posts.length > 0 && (
        <div className="text-center py-8">
          <button 
            onClick={loadPosts}
            className="px-6 py-2 bg-gray-100 text-gray-600 rounded-full text-sm font-medium hover:bg-gray-200 transition-colors"
          >
            <RefreshCw size={16} className="inline mr-2" />
            Actualiser
          </button>
        </div>
      )}

      {/* Create Modal */}
      <CreateProPostModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onSubmit={handleCreatePost}
      />
    </div>
  );
}
